import { useState } from "react";
import { motion } from "framer-motion";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, } from "react-icons/fa";
import { TbDeviceLandlinePhone } from "react-icons/tb";
import Fixed from "./Fixed";
import Footer1 from "./Footer1";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    name: false,
    email: false,
    message: false,
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {
      name: !formData.name,
      email: !formData.email,
      message: !formData.message,
    };

    setErrors(newErrors);

    // If no errors, create WhatsApp message link
    if (!Object.values(newErrors).includes(true)) {
      const phoneNumber = "919090007108"; // Replace with the desired WhatsApp number
      const message = `Hello, I am ${formData.name}. My email is ${formData.email}. Here's my message: ${formData.message}`;
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank"); // Open WhatsApp chat
    }
  };

  return (
    <div>
      <Fixed />
      <div className="bg-gray-100 text-gray-900 py-16 px-6 pt-32">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-800">Contact Us</h1>
          <p className="mt-2 text-lg text-gray-600">
            Have questions? We’d love to hear from you.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Details */}
          <motion.div 
  initial={{ opacity: 0, x: -50 }} 
  animate={{ opacity: 1, x: 0 }} 
  transition={{ duration: 0.8 }}
  className="space-y-6 bg-white p-8 rounded-xl shadow-md border border-gray-200"
>
  <h2 className="text-2xl font-bold text-blue-600">Get In Touch</h2>
  <p className="text-gray-700 text-left">
    Feel free to reach out through any of the methods below.
  </p>

  <div className="space-y-4">
    <div className="flex items-center space-x-4 text-lg">
      <FaPhoneAlt className="text-blue-600 text-xl" />
      <span>+91-9090007108</span>
    </div>
    <div className="flex items-center space-x-4 text-lg">
      <TbDeviceLandlinePhone className="text-blue-600 text-xl" />
      <span>0866-3511964</span>
    </div>
    <div className="flex items-center space-x-4 text-lg">
      <FaEnvelope className="text-blue-600 text-xl" />
      <span>tech.support@jcsglobal.in</span>
    </div>
    <div className="flex items-center space-x-4 text-lg">
      <FaMapMarkerAlt className="text-blue-600 text-xl" />
      <span>Vijayawada, A.P, India</span>
    </div>
  </div>
</motion.div>



          {/* Contact Form */}
          <motion.form 
            initial={{ opacity: 0, x: 50 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8 }}
            onSubmit={handleSubmit}
            className="bg-white p-8 rounded-xl shadow-md border border-gray-200 space-y-6"
          >
            <h2 className="text-2xl font-semibold text-gray-800">Send a Message</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium">Full Name</label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full mt-2 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? "border-red-500" : ""}`}
                  placeholder="Your Name" 
                  required
                />
                {errors.name && <p className="text-red-500 text-sm">Full Name is required</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium">Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full mt-2 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? "border-red-500" : ""}`}
                  placeholder="Your Email" 
                  required
                />
                {errors.email && <p className="text-red-500 text-sm">Email is required</p>}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium">Message</label>
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={`w-full mt-2 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.message ? "border-red-500" : ""}`}
                rows="4" 
                placeholder="Write your message..."
                required
              ></textarea>
              {errors.message && <p className="text-red-500 text-sm">Message is required</p>}
            </div>

            <button 
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold py-3 rounded-lg shadow-md transition-transform transform hover:scale-105"
            >
              Send Message
            </button>
          </motion.form>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16 px-6 max-w-7xl mx-auto">
        <h2 className="text-3xl font-extrabold text-center text-gray-800">Frequently Asked Questions</h2>
        <div className="mt-8 space-y-6">
          <details className="border-b pb-4 cursor-pointer">
            <summary className="font-semibold text-lg">How long does it take to get a response?</summary>
            <p className="mt-2 text-gray-600">We typically respond within 24 hours.</p>
          </details>
          <details className="border-b pb-4 cursor-pointer">
            <summary className="font-semibold text-lg">Do you offer support outside business hours?</summary>
            <p className="mt-2 text-gray-600">Yes, we provide limited email support during non-business hours.</p>
          </details>
          <details className="border-b pb-4 cursor-pointer">
            <summary className="font-semibold text-lg">Where are you located?</summary>
            <p className="mt-2 text-gray-600">We are located at 76-16-53, Jojinagar, Vijayawada, A.P, India</p>
          </details>
        </div>
      </div>

      <div className="bg-gray-100 py-16 px-6">
        <h2 className="text-3xl font-extrabold text-center text-gray-800">Find Us on the Map</h2>
        <div className="mt-8 max-w-5xl mx-auto">
        <iframe
        className="w-full h-96 rounded-lg shadow-md"
        title="JCS Group Location"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2913.2920510599215!2d80.60116497366754!3d16.54478872653439!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a35efc61c0bc971%3A0x3dbd08b289656eb0!2sJCS%20Group!5e1!3m2!1sen!2sin!4v1739268024949!5m2!1sen!2sin"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
        </div>
      </div>

      <Footer1 />
    </div>
  );
}
